import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Clock, MapPin, Building, FileText, X, Users, Utensils, UserX, AlertTriangle, HeartPulse } from 'lucide-react';
import { staffAPI } from '../services/api';
import api from '../services/api';
import toast from '../utils/toast';
import LoadingSpinner from './UI/LoadingSpinner';
import Modal from './UI/Modal';
import SearchableCombobox from './UI/SearchableCombobox';
import FormSelect from './UI/FormSelect';
import PillToggle from './UI/PillToggle';
import { formatTime } from '../utils/dateUtils';
import { isExternalActivityName, isWorkStatusLeaveTypeName } from '../constants/leaveTypes';

// Parse a "YYYY-MM-DD" date string as local parts to avoid UTC-midnight off-by-one
const parseISODateParts = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const parts = raw.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return { year: parts[0], month: parts[1], day: parts[2] };
};

const formatExceptionDateDisplay = (raw) => {
  const parsed = parseISODateParts(raw);
  if (!parsed) return raw || '';
  const { year, month, day } = parsed;
  // Build a local-midnight date for month name lookup only
  const monthName = new Date(year, month - 1, day).toLocaleString('en-US', { month: 'short' });
  return `${String(day).padStart(2, '0')}-${monthName}-${year}`;
};

const ROExceptionModal = ({
  isOpen,
  exception,
  onClose,
  onSuccess,
  theme = 'employee',
  allowProvisionalHospitalisation = false,
  onProvisionalHospitalisation,
}) => {
  const [selectedSessions, setSelectedSessions] = useState({ AM: false, PM: false });
  const [locations, setLocations] = useState({ AM: '', PM: '' });
  const [workSiteIds, setWorkSiteIds] = useState({ AM: '', PM: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [creatingLeave, setCreatingLeave] = useState(false);
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState([]);
  const [loadingLeaveTypes, setLoadingLeaveTypes] = useState(false);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [leavePeriod, setLeavePeriod] = useState('full');
  const [showSplitSessions, setShowSplitSessions] = useState(false);
  const [workSites, setWorkSites] = useState([]);
  const [loadingWorkSites, setLoadingWorkSites] = useState(false);

  // Clock events already on the exception date that block this resolution.
  // Kept in the modal rather than a toast: HR needs to read the times and
  // reconcile them, which a 4-second toast cannot support.
  const [clockEventConflict, setClockEventConflict] = useState(null);

  // Absent-family resolution mode: 'verify' (assert work / work-status leave)
  // vs 'confirm_absence' (employee genuinely did not work and had no leave —
  // recorded as Unpaid Leave at payroll via absence_proration).
  const [resolutionMode, setResolutionMode] = useState('verify');

  // Worker peer-based clock-out suggestion
  const [peerSuggestion, setPeerSuggestion] = useState(null);
  const [loadingPeerSuggestion, setLoadingPeerSuggestion] = useState(false);
  const [selectedClockOut, setSelectedClockOut] = useState(null);
  const peerSuggestionLoadedRef = useRef(false);
  const workSitePrefilledRef = useRef(false);

  // Missing-clockout: optional "Override worker's clock-in" toggle.
  // When checked, RO/HR is asserting a canonical session start time.
  // Backend then creates a new ro_resolution/hr_resolution clock_in
  // and supersedes ALL device clock_ins on the day (Mode A — see
  // .cursor/rules/ro-hr-resolution-source-of-truth.mdc). The W0199 /
  // 18-May-2026 pattern: worker only ever tapped at 21:00 (panic-tap
  // after realising he forgot the morning), so even his "earliest
  // clock-in" is wrong — RO knows he actually worked 08:00 -> 21:00
  // and must be able to assert that here.
  const [overrideClockIn, setOverrideClockIn] = useState(false);
  const [overrideClockInTime, setOverrideClockInTime] = useState('08:00');

  // Per-session Work/Leave mode and leave types
  const [amIsLeave, setAmIsLeave] = useState(false);
  const [pmIsLeave, setPmIsLeave] = useState(false);
  const [amLeaveTypeId, setAmLeaveTypeId] = useState('');
  const [pmLeaveTypeId, setPmLeaveTypeId] = useState('');

  // External Activity specific fields (shared across sessions)
  const [eaActivityType, setEaActivityType] = useState('');
  const [eaActivityLocation, setEaActivityLocation] = useState('');
  const [eaBusinessJustification, setEaBusinessJustification] = useState('');
  const [eaProjectCode, setEaProjectCode] = useState('');
  const [eaProjectName, setEaProjectName] = useState('');
  const [activeProjects, setActiveProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const projectsLoadedRef = useRef(false);

  // Track one-shot loading for work sites per modal lifetime
  const workSitesLoadedRef = useRef(false);

  // Fetch work sites when modal opens (one-shot)
  useEffect(() => {
    if (isOpen && !workSitesLoadedRef.current) {
      workSitesLoadedRef.current = true;
      loadWorkSites();
    }
  }, [isOpen]);

  // Fetch active projects when modal opens (one-shot, for External Activity project selector)
  useEffect(() => {
    if (isOpen && !projectsLoadedRef.current) {
      projectsLoadedRef.current = true;
      setLoadingProjects(true);
      staffAPI.getActiveProjects()
        .then(res => setActiveProjects(res.data?.results || res.data || []))
        .catch(() => {})
        .finally(() => setLoadingProjects(false));
    }
  }, [isOpen]);

  // Stable staffId used for leave types fetching
  const staffIdForLeaveTypes = (
    exception?.staff_id ||
    exception?.employee_staff_id ||
    exception?.employee_id ||
    exception?.staff?.staff_id ||
    exception?.employee?.staff_id ||
    ''
  ) ? String(
    exception?.staff_id ||
    exception?.employee_staff_id ||
    exception?.employee_id ||
    exception?.staff?.staff_id ||
    exception?.employee?.staff_id
  ) : '';
  const displayStaffId = staffIdForLeaveTypes;

  const lastLeaveTypesStaffIdRef = useRef(null);

  // Fetch available leave types when modal opens (keyed by staffId)
  useEffect(() => {
    if (!isOpen) return;
    const exType = String(exception?.type || exception?.exception_type || '').toLowerCase();
    if (exType === 'missing_clockout') return;
    // Avoid refetching if we already fetched for this staff in this session
    if (lastLeaveTypesStaffIdRef.current === staffIdForLeaveTypes && availableLeaveTypes.length > 0) return;

    let cancelled = false;
    const fetchLeaveTypes = async () => {
      try {
        setLoadingLeaveTypes(true);
        let res;
        if (staffIdForLeaveTypes) {
          res = await staffAPI.getAvailableLeaveTypes({ staff_id: staffIdForLeaveTypes, strict: 1 });
        } else {
          // Fallback to current user if staff id is unavailable
          res = await staffAPI.getAvailableLeaveTypes();
        }
        if (cancelled) return;
        const types = res?.data?.available_leave_types || [];
        const workStatusOnly = types.filter(t =>
          isWorkStatusLeaveTypeName(t.name || t.leave_type_name || '')
        );
        setAvailableLeaveTypes(workStatusOnly);
        lastLeaveTypesStaffIdRef.current = staffIdForLeaveTypes || 'self';
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load leave types', e);
        setAvailableLeaveTypes([]);
      } finally {
        if (!cancelled) setLoadingLeaveTypes(false);
      }
    };

    fetchLeaveTypes();
    return () => { cancelled = true; };
  }, [isOpen, staffIdForLeaveTypes]);

  // Reset all form state when exception changes (switching employees)
  useEffect(() => {
    if (!exception?.id) return;
    
    // Reset all form fields to initial state
    setSelectedSessions({ AM: false, PM: false });
    setLocations({ AM: '', PM: '' });
    setWorkSiteIds({ AM: '', PM: '' });
    setClockEventConflict(null);
    setNotes('');
    setReasonCode('');
    setLeaveTypeId('');
    setLeavePeriod('full');
    setShowSplitSessions(false);
    setAmIsLeave(false);
    setPmIsLeave(false);
    setAmLeaveTypeId('');
    setPmLeaveTypeId('');
    setEaActivityType('');
    setEaActivityLocation('');
    setEaBusinessJustification('');
    setEaProjectCode('');
    setEaProjectName('');
    setPeerSuggestion(null);
    setSelectedClockOut(null);
    setOverrideClockIn(false);
    setOverrideClockInTime('08:00');
    setResolutionMode('verify');
    peerSuggestionLoadedRef.current = false;
    workSitePrefilledRef.current = false;
  }, [exception?.id]);

  // Fetch peer clock-out suggestion for workers with missing clock-out
  useEffect(() => {
    if (!isOpen || !exception?.id) return;
    const exType = String(exception?.type || exception?.exception_type || '').toLowerCase();
    const classification = (exception?.employment_classification || '').toLowerCase();
    if (exType !== 'missing_clockout' || classification !== 'worker') return;
    if (peerSuggestionLoadedRef.current) return;

    peerSuggestionLoadedRef.current = true;
    let cancelled = false;
    const fetchPeerSuggestion = async () => {
      try {
        setLoadingPeerSuggestion(true);
        const res = await staffAPI.getPeerClockOutSuggestion(exception.id);
        if (cancelled) return;
        const data = res.data;
        setPeerSuggestion(data);
        // Pre-select largest peer cluster, or schedule default if no peers
        if (data.peer_clusters?.length > 0) {
          setSelectedClockOut(data.peer_clusters[0].clock_out_time);
        } else {
          setSelectedClockOut(data.schedule_default?.clock_out_time || '17:00');
        }
        // Pre-fill the override clock-in time picker with the worker's
        // scheduled AM start (e.g. 08:00 for workman, 08:30 for staff).
        // The user only toggles the override on when they need it.
        if (data.schedule_default?.clock_in_time) {
          setOverrideClockInTime(data.schedule_default.clock_in_time);
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load peer suggestion', e);
        setPeerSuggestion(null);
      } finally {
        if (!cancelled) setLoadingPeerSuggestion(false);
      }
    };
    fetchPeerSuggestion();
    return () => { cancelled = true; };
  }, [isOpen, exception?.id, exception?.type, exception?.exception_type, exception?.employment_classification]);

  // Absent / Verify Attendance: show finish/OT options as soon as PM work is
  // selected. Seed schedule-default + hour buckets immediately so HR can
  // declare OT before picking a site; when a work site is chosen, enrich
  // with same-project peer clusters.
  useEffect(() => {
    if (!isOpen || !exception?.id) return;
    const exType = String(exception?.type || exception?.exception_type || '').toLowerCase();
    const classification = (
      exception?.employment_classification
      || exception?.employee_employment_classification
      || ''
    ).toLowerCase();
    const isAbsentFamily = ['absent_no_leave', 'absent', 'absent_am', 'absent_pm'].includes(exType);
    if (!isAbsentFamily || classification !== 'worker') return;
    if (resolutionMode !== 'verify') return;

    const workSiteId = workSiteIds.AM || workSiteIds.PM;
    const includesPmWork = selectedSessions.PM && !pmIsLeave;
    // Full-day leave or AM-only work: no OT picker
    if (!includesPmWork) {
      setPeerSuggestion(null);
      setSelectedClockOut(null);
      setLoadingPeerSuggestion(false);
      return;
    }

    // Seed local defaults immediately so the picker is visible before / without
    // a work site (peers load once a site is selected).
    const localDefault = {
      schedule_default: {
        clock_out_time: '17:00',
        label: '5 PM',
        is_overtime: false,
        overtime_hours: 0,
        dinner_break_taken: false,
        worker_count: 0,
        is_schedule_default: true,
      },
      peer_clusters: [],
      clock_in_context: {},
      total_peers: 0,
    };

    if (!workSiteId) {
      setPeerSuggestion(localDefault);
      setSelectedClockOut((prev) => prev || '17:00');
      setLoadingPeerSuggestion(false);
      return;
    }

    let cancelled = false;
    const fetchPeerSuggestion = async () => {
      try {
        setLoadingPeerSuggestion(true);
        const res = await staffAPI.getPeerClockOutSuggestion(exception.id, {
          work_site_id: workSiteId,
        });
        if (cancelled) return;
        const data = res.data;
        setPeerSuggestion(data);
        // Keep HR's current selection if still valid; otherwise prefer largest peer cluster
        setSelectedClockOut((prev) => {
          const catalogIds = [];
          for (let h = 17; h <= 29; h += 1) {
            catalogIds.push(`${String(h).padStart(2, '0')}:00`);
          }
          const validIds = new Set([
            data.schedule_default?.clock_out_time,
            ...(data.peer_clusters || []).map((c) => c.clock_out_time),
            ...catalogIds,
          ].filter(Boolean));
          if (prev && validIds.has(prev)) return prev;
          if (data.peer_clusters?.length > 0) return data.peer_clusters[0].clock_out_time;
          return data.schedule_default?.clock_out_time || '17:00';
        });
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load peer suggestion for verify attendance', e);
        setPeerSuggestion(localDefault);
        setSelectedClockOut((prev) => prev || '17:00');
      } finally {
        if (!cancelled) setLoadingPeerSuggestion(false);
      }
    };
    fetchPeerSuggestion();
    return () => { cancelled = true; };
  }, [
    isOpen,
    exception?.id,
    exception?.type,
    exception?.exception_type,
    exception?.employment_classification,
    exception?.employee_employment_classification,
    workSiteIds.AM,
    workSiteIds.PM,
    selectedSessions.PM,
    pmIsLeave,
    resolutionMode,
  ]);

  // Auto-select the clock-in work site for missing_clockout exceptions.
  // HR almost always resolves a forgotten clock-out at the SAME site the
  // employee clocked in at, so we pre-fill it from clock_in_details. The
  // id is stored raw (matching the work-site list item type) and only
  // pre-filled once per exception so a manual change is never clobbered if
  // the work-site list refetches.
  useEffect(() => {
    if (!isOpen || !exception?.id) return;
    if (workSitePrefilledRef.current) return;
    const exType = String(exception?.type || exception?.exception_type || '').toLowerCase();
    if (exType !== 'missing_clockout') return;
    const clockIn = exception.clock_in_details;
    if (!clockIn || clockIn.work_site_id == null) return;
    const siteName = clockIn.work_site_name || clockIn.location || '';
    setLocations({ AM: siteName, PM: siteName });
    setWorkSiteIds({ AM: clockIn.work_site_id, PM: clockIn.work_site_id });
    workSitePrefilledRef.current = true;
  }, [isOpen, exception?.id, exception?.type, exception?.exception_type, exception?.clock_in_details]);

  const loadWorkSites = async () => {
    try {
      setLoadingWorkSites(true);
      const response = await api.get('/work-sites/');
      const workSitesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.results || []);
      
      // Filter to only active work sites
      const activeWorkSites = workSitesData.filter(site => 
        site.site_status === 'active'
      );
      
      setWorkSites(activeWorkSites);
    } catch (error) {
      console.error('Error loading work sites:', error);
      toast.error('Failed to load work site locations');
      // Set some default locations as fallback
      setWorkSites([
        { id: 'main-office', site_name: 'Main Office', site_address: '' },
        { id: 'site-a', site_name: 'Site A', site_address: '' },
        { id: 'site-b', site_name: 'Site B', site_address: '' },
        { id: 'client-location', site_name: 'Client Location', site_address: '' },
        { id: 'remote-work', site_name: 'Remote Work', site_address: '' }
      ]);
    } finally {
      setLoadingWorkSites(false);
    }
  };

  // Leave-covered session filtering (must be before early return — hooks cannot be conditional)
  // Backend returns [{session: 'AM', leave_type: 'Annual Leave'}, ...] or legacy ['AM', ...]
  const leaveCoveredRaw = exception?.leave_covered_sessions;
  const leaveCoveredMap = useMemo(() => {
    const m = {};
    if (!leaveCoveredRaw) return m;
    for (const entry of leaveCoveredRaw) {
      if (typeof entry === 'string') {
        m[entry] = 'Leave';
      } else if (entry?.session) {
        m[entry.session] = entry.leave_type || 'Leave';
      }
    }
    return m;
  }, [leaveCoveredRaw]);
  const amCoveredByLeave = 'AM' in leaveCoveredMap;
  const pmCoveredByLeave = 'PM' in leaveCoveredMap;

  const bothCoveredByLeave = amCoveredByLeave && pmCoveredByLeave;

  // Per-session coverage detail for absent_am / absent_pm: tells us how
  // the OTHER (covered) session was actually covered (clock event with
  // time + site, an approved leave, or a NW/RD AM-only recovery). Drives
  // the informative subtitle and the coverage chip; also lets us hide
  // the non-actionable session card so the RO is not asked to "fix"
  // a session that already has a documented source.
  const sessionCoverage = exception?.session_coverage || null;

  const exceptionTypeRaw = String(exception?.type || exception?.exception_type || '').toLowerCase();
  // The three absent-family types share the same "verify attendance / apply
  // backdated leave" workflow. absent_am and absent_pm are session-aware
  // variants of absent_no_leave -- see staff_core/models.py.
  const isAbsentForAutoSelect = (
    exceptionTypeRaw === 'absent_no_leave'
    || exceptionTypeRaw === 'absent_am'
    || exceptionTypeRaw === 'absent_pm'
    || exceptionTypeRaw === 'absent'
  );

  const availableSessionOptions = useMemo(() => {
    if (bothCoveredByLeave) {
      return [];
    }
    // Session-aware absence types pin the picker to the missed session.
    // The detector only fires absent_am / absent_pm when the OTHER session
    // is already covered (clock event or leave) -- offering "PM Only" /
    // "Full Day" on absent_am would let the RO contradict the detector's
    // own coverage finding. See AttendanceException.detect_absent_no_leave
    // in staff_core/models.py.
    if (exceptionTypeRaw === 'absent_am') {
      return [{ id: 'AM', label: 'AM Only' }];
    }
    if (exceptionTypeRaw === 'absent_pm') {
      return [{ id: 'PM', label: 'PM Only' }];
    }
    if (amCoveredByLeave && !pmCoveredByLeave) {
      return [{ id: 'PM', label: 'PM Only' }];
    }
    if (!amCoveredByLeave && pmCoveredByLeave) {
      return [{ id: 'AM', label: 'AM Only' }];
    }
    return [
      { id: 'AM', label: 'AM Only' },
      { id: 'PM', label: 'PM Only' },
      { id: 'FULL', label: 'Full Day' },
    ];
  }, [amCoveredByLeave, pmCoveredByLeave, bothCoveredByLeave, exceptionTypeRaw]);

  const setSessionsPreset = (preset) => {
    if (preset === 'AM') {
      setSelectedSessions({ AM: true, PM: false });
      setShowSplitSessions(false);
    } else if (preset === 'PM') {
      setSelectedSessions({ AM: false, PM: true });
      setShowSplitSessions(false);
    } else if (preset === 'FULL') {
      setSelectedSessions({ AM: true, PM: true });
      setShowSplitSessions(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !isAbsentForAutoSelect) return;
    // Session-aware exception types pre-select the missed session so the RO
    // doesn't have to re-pick it. Falls through to the legacy "single
    // available option" auto-select when type is generic absent_no_leave.
    if (exceptionTypeRaw === 'absent_am'
        && availableSessionOptions.some((o) => o.id === 'AM')) {
      setSessionsPreset('AM');
      return;
    }
    if (exceptionTypeRaw === 'absent_pm'
        && availableSessionOptions.some((o) => o.id === 'PM')) {
      setSessionsPreset('PM');
      return;
    }
    if (availableSessionOptions.length === 1) {
      setSessionsPreset(availableSessionOptions[0].id);
    }
  }, [isOpen, isAbsentForAutoSelect, exceptionTypeRaw, availableSessionOptions]);

  if (!isOpen || !exception) return null;

  // Normalize exception type for conditional UI
  const exceptionType = exceptionTypeRaw;
  const isMissingClockOut = exceptionType === 'missing_clockout';
  const isAbsent = isAbsentForAutoSelect;
  const isLate = exceptionType === 'late_arrival';
  const isEarly = exceptionType === 'early_departure';
  const isGeofence = exceptionType === 'geofence_violation' || exceptionType === 'geofence';
  const isWorkerEmployee = (
    exception?.employment_classification
    || exception?.employee_employment_classification
    || ''
  ).toLowerCase() === 'worker';

  // Confirm-absence mode is only meaningful for absent-family exceptions that
  // still have a missed session to account for (not when both halves are
  // already covered by approved leave).
  const isConfirmAbsenceMode = isAbsent && resolutionMode === 'confirm_absence';
  const confirmAbsenceScopeText = exceptionType === 'absent_am'
    ? 'The AM session will be recorded as an unpaid absence (Unpaid Leave). The PM session is unchanged.'
    : exceptionType === 'absent_pm'
      ? 'The PM session will be recorded as an unpaid absence (Unpaid Leave). The AM session is unchanged.'
      : 'This full day will be recorded as an unpaid absence (Unpaid Leave).';

  // Format exception type for display (user-friendly labels)
  const formatExceptionType = (type) => {
    const typeMap = {
      'absent_no_leave': 'Absent',
      'absent_am': 'Absent (AM)',
      'absent_pm': 'Absent (PM)',
      'missing_clockout': 'Missing Clock-Out',
      'late_arrival': 'Late Arrival',
      'early_departure': 'Early Departure',
      'geofence_violation': 'Geofence Violation'
    };
    return typeMap[type] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const modalTitle = isMissingClockOut
    ? 'Fix Missing Clock-Out'
    : isLate
      ? 'Fix Late Arrival'
      : isEarly
        ? 'Fix Early Departure'
        : isAbsent
          ? 'Verify Attendance'
          : isGeofence
            ? 'Review Geofence Violation'
            : 'Fix Missing Attendance';

  // Format a backend "HH:MM" clock-time as "h:mm AM/PM" for display.
  const formatClockTime = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return '';
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
    const period = h < 12 ? 'AM' : 'PM';
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
  };

  // Build a specific, informative subtitle for absent_am / absent_pm
  // exceptions using the backend-resolved coverage source. The previous
  // copy (clock event or leave) forced the RO to guess; this version
  // names the actual fact so they can verify before resolving.
  const buildSessionCoverageSubtitle = () => {
    if (!sessionCoverage || !sessionCoverage.coverage) return null;
    const { missed, covered, coverage } = sessionCoverage;
    const missedLabel = `Missed ${missed} session`;

    if (coverage.source === 'leave') {
      const period = coverage.leave_period;
      if (period === 'FULL') {
        return `${missedLabel} — ${covered} is approved ${coverage.leave_type} (full-day leave covering this date)`;
      }
      // Half-day leave that happens to land on the covered session.
      return `${missedLabel} — ${covered} is approved ${coverage.leave_type} (half-day)`;
    }

    if (coverage.source === 'recovery') {
      return `${missedLabel} — ${covered} was ${coverage.recovery_label}; ${missed} attendance was still required`;
    }

    if (coverage.source === 'clock_event') {
      const inT = formatClockTime(coverage.clock_in_time);
      const outT = formatClockTime(coverage.clock_out_time);
      const site = coverage.work_site_name;
      const sitePart = site ? ` at ${site}` : '';
      if (inT && outT) {
        return `${missedLabel} — clocked in at ${inT} and out at ${outT}${sitePart}`;
      }
      if (inT) {
        return `${missedLabel} — clocked in at ${inT}${sitePart}`;
      }
      if (outT) {
        return `${missedLabel} — clocked out at ${outT}${sitePart}`;
      }
      return `${missedLabel} — present in ${covered} session${sitePart}`;
    }

    return null;
  };

  const absentSubtitle = (() => {
    if (exceptionType === 'absent_am' || exceptionType === 'absent_pm') {
      // Prefer the coverage-aware copy; fall back to the legacy generic
      // string only if the backend couldn't resolve a source (e.g. data
      // changed between detection and modal open).
      return (
        buildSessionCoverageSubtitle()
        || (exceptionType === 'absent_am'
              ? 'Missed AM session — PM is already covered'
              : 'Missed PM session — AM is already covered')
      );
    }
    return 'No attendance record and no approved leave';
  })();

  const subtitleText = isMissingClockOut
    ? 'Employee is clocked in without a recorded clock-out'
    : isLate
      ? 'Employee clocked in later than scheduled'
      : isEarly
        ? 'Employee clocked out earlier than scheduled'
        : isAbsent
          ? absentSubtitle
          : isGeofence
            ? 'Attendance event occurred outside the allowed geofence'
            : 'Employee forgot to clock in/out but worked on this day';

  // Get time slots based on employee classification
  const getTimeSlots = (classification) => {
    const slots = {
      worker: { AM: '8:00 AM - 12:00 PM', PM: '1:00 PM - 5:00 PM' },
      staff: { AM: '8:30 AM - 12:30 PM', PM: '1:30 PM - 5:30 PM' },
      intern: { AM: '8:30 AM - 12:30 PM', PM: '1:30 PM - 5:30 PM' },
      executive_director: { AM: '8:30 AM - 12:30 PM', PM: '1:30 PM - 5:30 PM' },
    };
    return slots[classification?.toLowerCase()] || slots.staff;
  };

  const timeSlots = getTimeSlots(exception.employment_classification);
  const defaultClockOut = exception?.missing_clockout_default_clock_out || null;
  const defaultClockOutTime = defaultClockOut?.clock_out_time || null;
  const defaultClockOutLabel = defaultClockOut?.label || null;
  const defaultClockOutCaption = defaultClockOut?.source === 'classification_default'
    ? `${(exception.employment_classification || 'staff').replace(/_/g, ' ')} default`
    : 'schedule default';

  // Missing clock-out default: when the PM session is covered by approved
  // leave (e.g. an auto-approved External Activity half-day) the employee
  // worked AM only and left for the leave-covered PM. Default the clock-out
  // to the backend-resolved value so half-day Saturdays and overrides match
  // what set_clock_out writes.
  const pmCoveredOnly = pmCoveredByLeave && !amCoveredByLeave;
  const effectiveClockOutLabel = defaultClockOutLabel || (pmCoveredOnly
    ? timeSlots.AM.split(' - ')[1]
    : timeSlots.PM.split(' - ')[1]);

  const handleSessionToggle = (session) => {
    setSelectedSessions(prev => ({ ...prev, [session]: !prev[session] }));
  };

  const handleLocationChange = (session, value) => {
    setLocations(prev => ({ ...prev, [session]: value }));
  };

  const handleFullDayLocationChange = (value) => {
    setLocations(prev => ({ ...prev, AM: value, PM: value }));
  };

  const formatSiteLabel = (site) => {
    return site.project_code ? `${site.site_name} (${site.project_code})` : site.site_name;
  };

  const workSiteSearchFn = (site, term) =>
    site.site_name.toLowerCase().includes(term) ||
    (site.site_address && site.site_address.toLowerCase().includes(term)) ||
    (site.project_name && site.project_name.toLowerCase().includes(term)) ||
    (site.project_code && site.project_code.toLowerCase().includes(term));

  const renderWorkSiteItem = ({ item, isSelected, isHighlighted, onSelect }) => (
    <button
      key={item.id}
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
        isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
      } ${isHighlighted ? 'bg-gray-100' : ''}`}
    >
      <div className="font-medium">{item.site_name}</div>
      {item.project_code && (
        <div className="text-xs text-gray-500">{item.project_code} — {item.project_name || ''}</div>
      )}
    </button>
  );

  const handleWorkSiteChange = (session, siteId) => {
    const site = workSites.find(s => String(s.id) === String(siteId));
    const siteName = site ? site.site_name : '';
    if (session === 'FULL') {
      setLocations({ AM: siteName, PM: siteName });
      setWorkSiteIds({ AM: siteId, PM: siteId });
    } else if (session === 'AM') {
      setLocations(prev => ({ ...prev, AM: siteName }));
      setWorkSiteIds(prev => ({ ...prev, AM: siteId }));
    } else if (session === 'PM') {
      setLocations(prev => ({ ...prev, PM: siteName }));
      setWorkSiteIds(prev => ({ ...prev, PM: siteId }));
    }
  };

  const resolveDefaultWorkLeaveType = () => {
    const list = Array.isArray(availableLeaveTypes) ? availableLeaveTypes : [];
    return (list[0]?.id ?? list[0]?.leave_type_id ?? '');
  };

  // Resolve leave type name from ID (used for EA detection)
  const getLeaveTypeName = (id) => {
    if (!id) return '';
    const lt = availableLeaveTypes.find(t => String(t.id || t.leave_type_id) === String(id));
    return lt?.name || lt?.leave_type_name || '';
  };

  const isEALeaveTypeId = (id) => isExternalActivityName(getLeaveTypeName(id));

  // Build EA-specific payload fields (only when leave type is External Activity)
  const buildEAPayload = (leaveTypeId) => {
    if (!isEALeaveTypeId(leaveTypeId)) return {};
    return {
      activity_type: eaActivityType,
      activity_location: eaActivityLocation,
      business_justification: eaBusinessJustification,
      activity_project_code: eaProjectCode,
    };
  };

  // Activity types list (matches SmartLeaveApplicationModal)
  const EA_ACTIVITY_TYPES = [
    'Site Walk/Survey/Inspection',
    'Client/Consultant/Supplier/Coordination Meeting',
    'Conference/Seminar/Workshop/Training',
    'Tender Meeting/Presentation',
    'Government/Statutory Meeting',
    'Community/Charity/CSR Activity',
    'Annual Health Screening',
    'Other',
  ];

  // EA fields panel — shown beneath leave type selectors when External Activity is selected
  const renderEAFields = () => (
    <div className={`mt-3 p-3 border rounded-2xl space-y-3 ${theme === 'admin' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
      <p className={`text-xs font-semibold uppercase tracking-wide ${theme === 'admin' ? 'text-red-800' : 'text-blue-800'}`}>External Activity Details</p>

      {/* Activity Type */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          <Building className="inline w-3 h-3 mr-1" />
          Activity Type <span className="text-red-500">*</span>
        </label>
        <FormSelect
          value={eaActivityType}
          onChange={(newValue) => setEaActivityType(newValue)}
          options={[
            { value: '', label: 'Select activity type...' },
            ...EA_ACTIVITY_TYPES.map((type) => ({ value: type, label: type }))
          ]}
          theme={theme}
          placeholder="Select activity type..."
        />
      </div>

      {/* Project */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Project <span className="text-red-500">*</span>
        </label>
        {eaProjectCode ? (
          <div className={`flex items-center justify-between px-3 py-2 bg-white border rounded-full text-sm ${theme === 'admin' ? 'border-red-300' : 'border-blue-300'}`}>
            <div>
              <span className="font-medium text-gray-900">{eaProjectName}</span>
              {eaProjectCode !== 'Other' && (
                <span className="ml-2 text-xs text-gray-500 font-mono">{eaProjectCode}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setEaProjectCode(''); setEaProjectName(''); }}
              className={`ml-2 text-gray-400 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 rounded-full ${theme === 'admin' ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'}`}
              aria-label="Clear project"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <SearchableCombobox
            items={[{ project_code: 'Other', project_name: 'Other' }, ...activeProjects]}
            value={eaProjectCode}
            onChange={(code) => {
              const proj = [{ project_code: 'Other', project_name: 'Other' }, ...activeProjects]
                .find(p => p.project_code === code);
              setEaProjectCode(code);
              setEaProjectName(proj?.project_name || '');
            }}
            placeholder={loadingProjects ? 'Loading projects...' : 'Search project name or code...'}
            searchPlaceholder="Type to search..."
            emptyLabel="No projects found"
            displayFn={(p) => p.project_name}
            searchFn={(p, term) =>
              p.project_name?.toLowerCase().includes(term) ||
              (p.project_code && p.project_code !== 'Other' && p.project_code.toLowerCase().includes(term))
            }
            valueFn={(p) => p.project_code}
            renderItem={({ item, isSelected, isHighlighted, onSelect }) => (
              <button
                key={item.project_code}
                type="button"
                onClick={onSelect}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                  isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                } ${isHighlighted ? 'bg-gray-100' : ''}`}
              >
                <div className="font-medium">{item.project_name}</div>
                {item.project_code !== 'Other' && (
                  <div className="text-xs text-gray-500 font-mono">{item.project_code}</div>
                )}
              </button>
            )}
            disabled={loadingProjects}
            loading={loadingProjects}
            loadingLabel="Loading projects..."
            variant="form"
            theme={theme}
          />
        )}
      </div>

      {/* Activity Location */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          <MapPin className="inline w-3 h-3 mr-1" />
          Activity Location <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={eaActivityLocation}
          onChange={(e) => setEaActivityLocation(e.target.value)}
          className={`${theme === 'admin' ? 'input-admin' : 'input'} w-full`}
          placeholder="Where this activity takes place"
        />
      </div>

      {/* Purpose / Notes */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          <FileText className="inline w-3 h-3 mr-1" />
          Purpose / Notes <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={eaBusinessJustification}
          onChange={(e) => setEaBusinessJustification(e.target.value)}
          className={`${theme === 'admin' ? 'input-admin' : 'input'} w-full`}
          placeholder="Brief purpose or notes for this activity..."
        />
      </div>
    </div>
  );

  // Finish-time catalog for verify-attendance / missing-clockout OT.
  // Extended-hour notation matches the backend peer_suggestion buckets
  // (17 = 5 PM same day … 29 = 5 AM next day).
  const FINISH_HOUR_MIN = 17;
  const FINISH_HOUR_MAX = 29; // 5 AM next day — mirrors peer window upper bound

  const buildFinishOptionFromHour = (extendedHour, { workerCount = 0, isScheduleDefault = false } = {}) => {
    const gross = extendedHour - 17;
    const actualHour = extendedHour % 24;
    const isNextDay = extendedHour >= 24;
    const dinner = actualHour >= 21 || actualHour < 6;
    const net = gross <= 0 ? 0 : (dinner ? Math.max(0, gross - 1) : gross);
    const labelHour = actualHour % 12 || 12;
    const period = actualHour < 12 ? 'AM' : 'PM';
    const baseLabel = `${labelHour} ${period}${isNextDay ? ' (next day)' : ''}`;
    return {
      clock_out_time: `${String(extendedHour).padStart(2, '0')}:00`,
      label: baseLabel,
      is_overtime: net > 0,
      overtime_hours: net,
      dinner_break_taken: dinner && net > 0,
      worker_count: workerCount,
      is_schedule_default: isScheduleDefault,
      is_next_day: isNextDay,
      is_fallback: !isScheduleDefault && workerCount === 0,
    };
  };

  const buildAllFinishOptions = () => {
    const options = [];
    for (let h = FINISH_HOUR_MIN; h <= FINISH_HOUR_MAX; h += 1) {
      options.push(buildFinishOptionFromHour(h, { isScheduleDefault: h === 17 }));
    }
    return options;
  };

  // Pill track: schedule + peer clusters only (DS: >3 complex choices → dropdown).
  const getPeerOtPillOptions = () => {
    if (!peerSuggestion) return [];
    const formatPickerLabel = (label, isNextDay) => {
      if (isNextDay && typeof label === 'string') {
        return label.replace(' (next day)', '+');
      }
      return label;
    };
    return [
      {
        id: peerSuggestion.schedule_default.clock_out_time,
        label: peerSuggestion.schedule_default.label,
        title: peerSuggestion.schedule_default.label,
        count: peerSuggestion.schedule_default.worker_count || undefined,
      },
      ...(peerSuggestion.peer_clusters || []).map((c) => ({
        id: c.clock_out_time,
        label: formatPickerLabel(c.label, c.is_next_day),
        title: c.label,
        count: c.worker_count,
      })),
    ];
  };

  // Full catalog for FormSelect — every hour HR may need, with OT preview.
  const getFinishTimeSelectOptions = () => {
    if (!peerSuggestion) return [];
    const peerByTime = Object.fromEntries(
      (peerSuggestion.peer_clusters || []).map((c) => [c.clock_out_time, c])
    );
    const scheduleKey = peerSuggestion.schedule_default?.clock_out_time;
    const scheduleCount = peerSuggestion.schedule_default?.worker_count || 0;

    return buildAllFinishOptions().map((opt) => {
      const peer = peerByTime[opt.clock_out_time];
      const count = peer?.worker_count
        || (opt.clock_out_time === scheduleKey ? scheduleCount : 0);
      const otPart = opt.is_overtime ? `${opt.overtime_hours}h OT` : 'No OT';
      const peerPart = count > 0 ? ` · ${count} peer${count !== 1 ? 's' : ''}` : '';
      return {
        value: opt.clock_out_time,
        label: `${opt.label} — ${otPart}${peerPart}`,
      };
    });
  };

  const getSelectedClockOutOption = () => {
    if (!peerSuggestion || !selectedClockOut) return null;
    if (selectedClockOut === peerSuggestion.schedule_default?.clock_out_time) {
      return {
        ...peerSuggestion.schedule_default,
        is_schedule_default: true,
      };
    }
    const fromPeers = peerSuggestion.peer_clusters?.find((c) => c.clock_out_time === selectedClockOut);
    if (fromPeers) return fromPeers;
    const hour = parseInt(String(selectedClockOut).split(':')[0], 10);
    if (!Number.isNaN(hour) && hour >= FINISH_HOUR_MIN && hour <= FINISH_HOUR_MAX) {
      return buildFinishOptionFromHour(hour);
    }
    return null;
  };

  const renderPeerOtPicker = ({ sectionLabel = 'Finish time / OT' } = {}) => {
    if (loadingPeerSuggestion) {
      return (
        <div className="flex items-center justify-center py-4">
          <LoadingSpinner size="sm" />
          <span className="ml-2 text-sm text-gray-500">Loading peer OT data...</span>
        </div>
      );
    }
    if (!peerSuggestion) return null;
    const sel = getSelectedClockOutOption();
    const hasPeers = (peerSuggestion.total_peers || 0) > 0;
    const pillIds = new Set(getPeerOtPillOptions().map((o) => o.id));
    const isCustomFinish = selectedClockOut && !pillIds.has(selectedClockOut);

    return (
      <div className="mt-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <label className="block text-xs font-medium text-gray-700">{sectionLabel}</label>
          {peerSuggestion.clock_in_context?.project_name && hasPeers && (
            <span className="text-xs text-gray-400 flex items-center gap-1 min-w-0 truncate">
              <Users className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{peerSuggestion.clock_in_context.project_name} peers</span>
            </span>
          )}
        </div>

        {/* Fast path: schedule + peer clusters — fullWidth spreads a small
            set of pills across the track (DS). Full hour catalog lives in
            FormSelect below so we never pack 10+ chips here. */}
        <PillToggle
          value={pillIds.has(selectedClockOut) ? selectedClockOut : ''}
          onChange={setSelectedClockOut}
          options={getPeerOtPillOptions()}
          variant="text"
          theme={theme}
          className="w-full"
          fullWidth
          ariaLabel="Peer-suggested finish times"
        />

        {/* Full catalog: 5 PM → 5 AM next day (DS: many choices → FormSelect) */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Or pick another finish time
          </label>
          <FormSelect
            value={selectedClockOut || ''}
            onChange={setSelectedClockOut}
            options={getFinishTimeSelectOptions()}
            theme={theme}
            placeholder="All finish times..."
          />
          <p className="text-xs text-gray-400 mt-1.5">
            Includes overnight finishes through 5 AM next day. OT is net of dinner when finish is 9 PM or later.
          </p>
        </div>

        {sel && (
          <div className={`rounded-2xl px-4 py-3 border ${sel.is_overtime
            ? 'bg-amber-50 border-amber-200'
            : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className={`w-3.5 h-3.5 ${sel.is_overtime ? 'text-amber-600' : 'text-gray-500'}`} />
                <span className="text-sm font-medium text-gray-900">
                  {sel.is_overtime ? `${sel.overtime_hours}h Overtime` : 'No Overtime'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {sel.dinner_break_taken && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                    <Utensils className="w-3 h-3" />
                    Dinner −1h
                  </span>
                )}
                {sel.worker_count > 0 && (
                  <span className="text-xs text-gray-500">
                    {sel.worker_count} worker{sel.worker_count !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            {!hasPeers && (
              <p className="text-xs text-gray-500 mt-2">
                {(workSiteIds.AM || workSiteIds.PM)
                  ? 'No peer finishes at this site today — pick the finish time that matches what the worker actually worked.'
                  : 'Select a work site to load peer OT suggestions. You can still pick a finish time now.'}
              </p>
            )}
            {isCustomFinish && (
              <p className="text-xs text-gray-500 mt-2">
                Manual finish (not a peer cluster). OT is calculated from schedule end.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async () => {
    setClockEventConflict(null);

    // Confirm unpaid absence: no clock events / no leave created. The day
    // stays 'absent' and renders as Unpaid Leave at payroll. We only resolve
    // the exception so it leaves the RO dashboard.
    if (isConfirmAbsenceMode) {
      setIsSubmitting(true);
      try {
        await staffAPI.confirmExceptionAbsence(exception.id, {
          reason_code: reasonCode,
          notes,
        });
        toast.success('Absence confirmed — recorded as Unpaid Leave');
        if (onSuccess) onSuccess();
        onClose();
      } catch (error) {
        console.error('Failed to confirm absence:', error);
        const errorMsg = error?.response?.data?.error || 'Failed to confirm absence';
        toast.error(`❌ ${errorMsg}`);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Missing clock-out: call the dedicated set_clock_out endpoint
    if (isMissingClockOut) {
      const isWorker = (exception?.employment_classification || '').toLowerCase() === 'worker';

      if (isWorker && peerSuggestion) {
        // Worker peer-based flow: send selected cluster data
        const option = getSelectedClockOutOption();
        if (!option) {
          toast.error('Please select a clock-out time');
          return;
        }
        // Validate override clock-in if enabled (Mode A — canonical RO session)
        if (overrideClockIn) {
          if (!/^\d{2}:\d{2}$/.test(overrideClockInTime)) {
            toast.error('Override clock-in time must be HH:MM');
            return;
          }
          if (overrideClockInTime >= option.clock_out_time) {
            toast.error('Override clock-in time must be before the clock-out time');
            return;
          }
        }
        setIsSubmitting(true);
        try {
          const payload = {
            clock_out_time: option.clock_out_time,
            is_overtime: option.is_overtime,
            overtime_hours: option.overtime_hours,
            dinner_break_taken: option.dinner_break_taken,
            notes,
            reason_code: reasonCode,
          };
          if (overrideClockIn) {
            payload.clock_in_time = overrideClockInTime;
          }
          const res = await staffAPI.setExceptionClockOut(exception.id, payload);
          if (res.data?.already_resolved) {
            toast.success('Clock-out already existed — exception resolved');
          } else {
            const label = option.is_overtime
              ? `Clock-out set at ${option.label} (${option.overtime_hours}h OT)`
              : `Clock-out set at ${option.label}`;
            toast.success(label);
          }
          if (onSuccess) onSuccess();
          onClose();
        } catch (error) {
          console.error('Failed to set clock-out:', error);
          const errorData = error?.response?.data;
          const errorMsg = errorData?.error || 'Failed to set clock-out';
          if (errorData?.code === 'payroll_locked') {
            toast.warning(errorMsg);
          } else {
            toast.error(errorMsg);
          }
        } finally {
          setIsSubmitting(false);
        }
        return;
      }

      // Non-worker legacy flow: requires work site selection
      if (!locations.AM) {
        toast.error('Please select a work site');
        return;
      }
      // Validate override clock-in if enabled (Mode A). Non-workers use the
      // backend-resolved schedule default, including Saturday half-days.
      if (overrideClockIn) {
        if (!/^\d{2}:\d{2}$/.test(overrideClockInTime)) {
          toast.error('Override clock-in time must be HH:MM');
          return;
        }
        if (defaultClockOutTime && overrideClockInTime >= defaultClockOutTime) {
          toast.error('Override clock-in time must be before the clock-out time');
          return;
        }
      }
      setIsSubmitting(true);
      try {
        const payload = {
          notes,
          reason_code: reasonCode,
          location: locations.AM || '',
          work_site_id: workSiteIds.AM || undefined,
        };
        if (overrideClockIn) {
          payload.clock_in_time = overrideClockInTime;
        }
        const res = await staffAPI.setExceptionClockOut(exception.id, payload);
        if (res.data?.already_resolved) {
          toast.success('Clock-out already existed — exception resolved');
        } else {
          toast.success('Clock-out set successfully');
        }
        if (onSuccess) onSuccess();
        onClose();
      } catch (error) {
        console.error('Failed to set clock-out:', error);
        const errorData = error?.response?.data;
        const errorMsg = errorData?.error || 'Failed to set clock-out';
        if (errorData?.code === 'payroll_locked') {
          toast.warning(errorMsg);
        } else {
          toast.error(errorMsg);
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Validation (for non-missing-clockout types)
    if (!selectedSessions.AM && !selectedSessions.PM) {
      toast.error('Please select at least one session (AM or PM)');
      return;
    }
    // Validation for Full Day (not split)
    if (selectedSessions.AM && selectedSessions.PM && !showSplitSessions) {
      if (!amIsLeave && !pmIsLeave) {
        // Full Day Work mode
        const fullDayLocation = locations.AM && locations.PM && locations.AM === locations.PM ? locations.AM : locations.AM || locations.PM;
        if (!fullDayLocation) {
          toast.error('Please select a work site for Full Day work');
          return;
        }
      } else if (amIsLeave && pmIsLeave) {
        // Full Day Leave mode
        const fullDayLeaveType = amLeaveTypeId || pmLeaveTypeId;
        if (!fullDayLeaveType) {
          toast.error('Please select a work leave type for Full Day');
          return;
        }
      }
    } else {
      // Individual session validations
      if (selectedSessions.AM) {
        if (!amIsLeave && !locations.AM) {
      toast.error('Please select a work site for AM session');
      return;
    }
        if (amIsLeave && !amLeaveTypeId) {
          toast.error('Please select a work leave type for AM');
          return;
        }
      }
      if (selectedSessions.PM) {
        if (!pmIsLeave && !locations.PM) {
      toast.error('Please select a work site for PM session');
      return;
        }
        if (pmIsLeave && !pmLeaveTypeId) {
          toast.error('Please select a work leave type for PM');
          return;
        }
      }
    }

    setIsSubmitting(true);

    try {
      // Build work sessions array (only those set to Work)
      const workSessions = [];

      // Handle Full Day (not split) - create separate AM and PM sessions
      if (selectedSessions.AM && selectedSessions.PM && !showSplitSessions) {
        // Full Day: create separate AM and PM sessions with same location
        const fullDayLocation = locations.AM && locations.PM && locations.AM === locations.PM ? locations.AM : locations.AM || locations.PM;
        const fullDayWorkSiteId = workSiteIds.AM && workSiteIds.PM && workSiteIds.AM === workSiteIds.PM ? workSiteIds.AM : workSiteIds.AM || workSiteIds.PM;
        if (fullDayLocation) {
          workSessions.push({ period: 'AM', location: fullDayLocation, work_site_id: fullDayWorkSiteId });
          workSessions.push({ period: 'PM', location: fullDayLocation, work_site_id: fullDayWorkSiteId });
        }
      } else {
        // Individual sessions (AM only, PM only, or split Full Day)
        if (selectedSessions.AM && !amIsLeave && locations.AM) {
          workSessions.push({ period: 'AM', location: locations.AM, work_site_id: workSiteIds.AM });
        }
        if (selectedSessions.PM && !pmIsLeave && locations.PM) {
          workSessions.push({ period: 'PM', location: locations.PM, work_site_id: workSiteIds.PM });
        }
      }

      if (workSessions.length > 0) {
        const payload = { sessions: workSessions, notes, reason_code: reasonCode };
        // Worker verify-attendance: attach peer-selected finish / OT when
        // PM work is included (same contract as set_clock_out).
        const isWorker = (exception?.employment_classification || '').toLowerCase() === 'worker';
        const includesPmWork = workSessions.some((s) => s.period === 'PM');
        if (isWorker && includesPmWork && selectedClockOut) {
          const option = getSelectedClockOutOption();
          if (option) {
            payload.clock_out_time = option.clock_out_time;
            payload.is_overtime = !!option.is_overtime;
            payload.overtime_hours = option.overtime_hours || 0;
            payload.dinner_break_taken = !!option.dinner_break_taken;
          }
        }
        await staffAPI.createExceptionSessions(exception.id, payload);
        const otOption = isWorker && includesPmWork ? getSelectedClockOutOption() : null;
        if (otOption?.is_overtime) {
          toast.success(`Attendance verified with ${otOption.overtime_hours}h OT`);
        } else {
          toast.success('Clock events created');
        }
        // Status is already set to 'resolved' by create_sessions backend action
      }

      // Create leave for sessions marked as Leave
      if (selectedSessions.AM && selectedSessions.PM && !showSplitSessions) {
        // Full Day leave (not split)
        const fullDayLeaveType = amLeaveTypeId || pmLeaveTypeId;
        if (amIsLeave || pmIsLeave) {
          await staffAPI.createExceptionLeave(exception.id, {
            leave_type_id: fullDayLeaveType,
            period: 'full',
            notes,
            reason_code: reasonCode,
            ...buildEAPayload(fullDayLeaveType),
          });
          toast.success('✅ Full Day Work Leave created');
          // Status is already set to 'resolved' by create_leave backend action
        }
      } else {
        // Individual session leaves
        const amLeave = selectedSessions.AM && amIsLeave ? { period: 'AM', leave_type_id: amLeaveTypeId } : null;
        const pmLeave = selectedSessions.PM && pmIsLeave ? { period: 'PM', leave_type_id: pmLeaveTypeId } : null;

        if (amLeave || pmLeave) {
          // If both halves are leave and same type, collapse into full day
          if (amLeave && pmLeave && (String(amLeave.leave_type_id) === String(pmLeave.leave_type_id))) {
            await staffAPI.createExceptionLeave(exception.id, {
              leave_type_id: amLeave.leave_type_id,
              period: 'full',
              notes,
              reason_code: reasonCode,
              ...buildEAPayload(amLeave.leave_type_id),
            });
            toast.success('✅ Work Leave created (Full Day)');
            // Status is already set to 'resolved' by create_leave backend action
          } else {
            if (amLeave) {
            await staffAPI.createExceptionLeave(exception.id, {
                leave_type_id: amLeave.leave_type_id,
                period: 'AM',
                notes,
                reason_code: reasonCode,
                ...buildEAPayload(amLeave.leave_type_id),
              });
              toast.success('✅ AM Work Leave created');
              // Status is already set to 'resolved' by create_leave backend action
            }
            if (pmLeave) {
            await staffAPI.createExceptionLeave(exception.id, {
                leave_type_id: pmLeave.leave_type_id,
                period: 'PM',
                notes,
                reason_code: reasonCode,
                ...buildEAPayload(pmLeave.leave_type_id),
              });
              toast.success('✅ PM Work Leave created');
              // Status is already set to 'resolved' by create_leave backend action
            }
          }
        }
      }
      
      // Reset form
      setSelectedSessions({ AM: false, PM: false });
      setLocations({ AM: '', PM: '' });
      setWorkSiteIds({ AM: '', PM: '' });
      setAmIsLeave(false);
      setPmIsLeave(false);
      setAmLeaveTypeId('');
      setPmLeaveTypeId('');
      
      // Notify parent to refresh data
      if (onSuccess) onSuccess();
      
      onClose();
    } catch (error) {
      console.error('Failed to create clock events:', error);
      const errorData = error?.response?.data;
      const errorMsg = errorData?.error || 'Failed to create clock events';
      if (errorData?.code === 'payroll_locked') {
        toast.warning(errorMsg);
      } else if (errorData?.code === 'conflicting_clock_events') {
        setClockEventConflict({
          message: errorMsg,
          events: errorData.conflicts || [],
          periods: errorData.duplicates || [],
        });
        toast.warning('Attendance is already recorded for part of this day');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateLeave = async () => {
    if (!leaveTypeId) {
      toast.error('Select a work leave type');
      return;
    }
    setCreatingLeave(true);
    try {
      await staffAPI.createExceptionLeave(exception.id, {
        leave_type_id: leaveTypeId,
        period: leavePeriod,
        notes,
        reason_code: reasonCode,
        ...buildEAPayload(leaveTypeId),
      });
      toast.success('✅ Work Leave created and exception verified');
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to create leave';
      toast.error(`❌ ${msg}`);
    } finally {
      setCreatingLeave(false);
    }
  };

  const handleSetStatus = async (statusValue) => {
    try {
      await staffAPI.setExceptionStatus(exception.id, { status: statusValue, notes });
      toast.success(`✅ Status set to ${statusValue}`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to set status';
      toast.error(`❌ ${msg}`);
    }
  };

  const handleClose = () => {
    // Reset all form state
    setSelectedSessions({ AM: false, PM: false });
    setLocations({ AM: '', PM: '' });
    setWorkSiteIds({ AM: '', PM: '' });
    setNotes('');
    setReasonCode('');
    setLeaveTypeId('');
    setLeavePeriod('full');
    setShowSplitSessions(false);
    setAmIsLeave(false);
    setPmIsLeave(false);
    setAmLeaveTypeId('');
    setPmLeaveTypeId('');
    setEaActivityType('');
    setEaActivityLocation('');
    setEaBusinessJustification('');
    setEaProjectCode('');
    setEaProjectName('');
    setPeerSuggestion(null);
    setSelectedClockOut(null);
    setResolutionMode('verify');
    setClockEventConflict(null);
    peerSuggestionLoadedRef.current = false;
    onClose();
  };

  const modalFooter = (
    <>
      <button
        type="button"
        onClick={handleClose}
        className={`btn ${theme === 'admin' ? 'btn-admin-outline' : 'btn-outline'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${theme === 'admin' ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'}`}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting || (isConfirmAbsenceMode
          ? false
          : isMissingClockOut
            ? ((exception?.employment_classification || '').toLowerCase() === 'worker' ? !selectedClockOut : !locations.AM)
            : (!selectedSessions.AM && !selectedSessions.PM))}
        className={`btn ${theme === 'admin' ? 'btn-admin-primary' : 'btn-primary'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${theme === 'admin' ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'}`}
      >
        {isSubmitting ? (
          <>
            <LoadingSpinner size="sm" color="white" />
            {isConfirmAbsenceMode ? 'Confirming...' : isMissingClockOut ? 'Setting clock-out...' : 'Creating...'}
          </>
        ) : (
          <>
            {isConfirmAbsenceMode ? <UserX className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
            {isConfirmAbsenceMode ? 'Confirm absence' : isMissingClockOut ? 'Set clock-out' : 'Verify'}
          </>
        )}
      </button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle} size="sm" theme={theme} footer={modalFooter}>
      <div className={isMissingClockOut ? 'space-y-4' : 'space-y-6'}>

          {isMissingClockOut ? (
          <>
          {/* Compact employee row + attendance context */}
          <div className={`rounded-2xl px-4 py-3 flex items-center justify-between gap-3 ${theme === 'admin' ? 'bg-red-50' : 'bg-blue-50'}`}>
            <div className="min-w-0">
              <p className="font-medium text-gray-900 text-sm truncate">{exception.employee_name || exception.staff_name}</p>
              {displayStaffId && (
                <p className="text-xs text-gray-500 mt-0.5">Staff ID: {displayStaffId}</p>
              )}
            </div>
            <span className="text-sm font-medium text-gray-600 flex-shrink-0 ml-3">
              {formatExceptionDateDisplay(exception.date || exception.exception_date)}
            </span>
          </div>

          {/* Clock-in context row */}
          <div className="rounded-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-green-50">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                <span className="text-gray-600">Clock-in</span>
              </div>
              <div className="text-sm font-medium text-gray-900 text-right">
                {exception.clock_in_details ? (
                  <>
                    {formatTime(exception.clock_in_details.clock_in_time)}
                    {(exception.clock_in_details.work_site_name || exception.clock_in_details.location) && (
                      <span className="text-gray-500 font-normal"> at {exception.clock_in_details.work_site_name || exception.clock_in_details.location}</span>
                    )}
                  </>
                ) : (
                  <span className="text-gray-400 font-normal">Not available</span>
                )}
              </div>
            </div>
          </div>

          {/* Approved-leave coverage notice — when a session is already
              covered by approved leave, the employee only worked the other
              half. The clock-out default below is adjusted accordingly. */}
          {(amCoveredByLeave || pmCoveredByLeave) && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2 text-sm">
                <FileText className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-amber-800">
                  {amCoveredByLeave && pmCoveredByLeave ? (
                    <>Both sessions are covered by approved leave (AM — {leaveCoveredMap['AM']}, PM — {leaveCoveredMap['PM']}).</>
                  ) : pmCoveredByLeave ? (
                    <>PM session is covered by <span className="font-medium">{leaveCoveredMap['PM']}</span> (approved). Clocking out at the end of the AM session.</>
                  ) : (
                    <>AM session is covered by <span className="font-medium">{leaveCoveredMap['AM']}</span> (approved).</>
                  )}
                </div>
              </div>
            </div>
          )}

          {(exception?.employment_classification || '').toLowerCase() === 'worker' ? (
          <>
          {/* Worker peer-based clock-out + full finish-time catalog */}
          {renderPeerOtPicker({ sectionLabel: 'Clock-out time' })}

          {/* Reason + Notes (shared by worker and non-worker) */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <FormSelect
                value={reasonCode}
                onChange={(newValue) => setReasonCode(newValue)}
                options={[
                  { value: '', label: 'Select reason...' },
                  { value: 'forgot_clockin_out', label: 'Forgot to clock out' },
                  { value: 'system_device_issue', label: 'System/device issue' },
                  { value: 'emergency', label: 'Emergency' },
                  { value: 'other', label: 'Others' },
                ]}
                theme={theme}
                placeholder="Select reason..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className={`w-full px-4 py-2.5 rounded-2xl border border-gray-300 text-sm focus:outline-none focus:ring-2 resize-none ${theme === 'admin' ? 'focus:ring-red-500 focus:border-red-600' : 'focus:ring-blue-500 focus:border-blue-800'}`}
                placeholder="Brief explanation"
              />
            </div>
          </div>
          </>
          ) : (
          <>
          {/* Non-worker: original clock-out row + work site picker */}
          <div className="rounded-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                <span className="text-gray-600">Clock-out</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-gray-900">{effectiveClockOutLabel}</span>
                <span className="text-xs text-gray-400 ml-1.5">
                    {pmCoveredOnly ? 'end of AM session' : defaultClockOutCaption}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Site</label>
              <SearchableCombobox
                items={workSites}
                value={workSiteIds.AM}
                onChange={(siteId) => handleWorkSiteChange('FULL', siteId)}
                placeholder="Select work site..."
                searchPlaceholder="Search by site name or project code..."
                emptyLabel="No work sites found"
                displayFn={formatSiteLabel}
                searchFn={workSiteSearchFn}
                valueFn={(site) => site.id}
                renderItem={renderWorkSiteItem}
                disabled={loadingWorkSites}
                loading={loadingWorkSites}
                loadingLabel="Loading work sites..."
                variant="form"
                theme={theme}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <FormSelect
                value={reasonCode}
                onChange={(newValue) => setReasonCode(newValue)}
                options={[
                  { value: '', label: 'Select reason...' },
                  { value: 'forgot_clockin_out', label: 'Forgot to clock out' },
                  { value: 'system_device_issue', label: 'System/device issue' },
                  { value: 'emergency', label: 'Emergency' },
                  { value: 'other', label: 'Others' },
                ]}
                theme={theme}
                placeholder="Select reason..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className={`w-full px-4 py-2.5 rounded-2xl border border-gray-300 text-sm focus:outline-none focus:ring-2 resize-none ${theme === 'admin' ? 'focus:ring-red-500 focus:border-red-600' : 'focus:ring-blue-500 focus:border-blue-800'}`}
                placeholder="Brief explanation"
              />
            </div>
          </div>
          </>
          )}
          </>
          ) : (
          <>
          {/* Employee Info Section */}
          <section className="space-y-4">
            <h4 className="text-base font-semibold text-gray-900">Employee Information</h4>
            <div className={`rounded-2xl p-4 space-y-3 ${theme === 'admin' ? 'bg-red-50' : 'bg-blue-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-base">{exception.employee_name || exception.staff_name}</p>
                  {displayStaffId && (
                    <p className="text-xs text-gray-500 mt-1">Staff ID: {displayStaffId}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{subtitleText}</p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full flex-shrink-0 ml-3 ${
                  exceptionType === 'absent_no_leave' ? 'bg-red-100 text-red-800' :
                  exceptionType === 'absent_am' ? 'bg-red-100 text-red-800' :
                  exceptionType === 'absent_pm' ? 'bg-red-100 text-red-800' :
                  exceptionType === 'missing_clockout' ? 'bg-yellow-100 text-yellow-800' :
                  exceptionType === 'late_arrival' ? 'bg-yellow-100 text-yellow-800' :
                  exceptionType === 'early_departure' ? 'bg-orange-100 text-orange-800' :
                  exceptionType === 'geofence_violation' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {formatExceptionType(exceptionType)}
                </span>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700">
                  {formatExceptionDateDisplay(exception.date || exception.exception_date)}
                </div>
              </div>
            </div>
          </section>

          {/* Resolution mode: verify (work / work-status leave) vs confirm
              unpaid absence. Hidden when both sessions are already covered by
              approved leave (nothing is absent to confirm). */}
          {!bothCoveredByLeave && (
            <section className="space-y-3">
              <h4 className="text-base font-semibold text-gray-900">How should this be resolved?</h4>
              <PillToggle
                value={resolutionMode}
                onChange={setResolutionMode}
                options={[
                  { id: 'verify', label: 'Verify attendance' },
                  { id: 'confirm_absence', label: 'Confirm absence' },
                ]}
                variant="text"
                theme={theme}
                ariaLabel="Resolution mode"
                fullWidth
              />
              <p className="text-xs text-gray-400">
                {resolutionMode === 'confirm_absence'
                  ? 'Use this when the employee did not work and had no leave for this date.'
                  : 'Record work done, or apply a work-status leave (WFH, External Activity, On Course).'}
              </p>
              {allowProvisionalHospitalisation && isAbsentForAutoSelect && (
                <button
                  type="button"
                  className={theme === 'admin' ? 'btn btn-admin-outline w-full' : 'btn btn-outline w-full'}
                  onClick={() => onProvisionalHospitalisation?.()}
                >
                  <HeartPulse className="w-4 h-4 mr-2" />
                  Hospitalised — certificate pending
                </button>
              )}
            </section>
          )}

          {/* Confirm unpaid absence panel */}
          {isConfirmAbsenceMode && (
            <section className="space-y-4">
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex items-start gap-2 text-sm">
                  <UserX className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-amber-800">
                    <p className="font-medium">{confirmAbsenceScopeText}</p>
                    <p className="text-xs text-amber-700 mt-1">
                      No clock events or leave are created. The day is treated as
                      unpaid time off — the employee is not paid for this session.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Session Selection Section */}
          {!isConfirmAbsenceMode && (
          <section className="space-y-4">
            <h4 className="text-base font-semibold text-gray-900">Work Sessions</h4>

            {bothCoveredByLeave ? (
              <div className="p-4 bg-gray-50 rounded-xl text-center space-y-1">
                <p className="text-sm font-medium text-gray-700">Both sessions are covered by approved leave</p>
                <p className="text-xs text-gray-400">
                  AM — {leaveCoveredMap['AM']} &middot; PM — {leaveCoveredMap['PM']}
                </p>
                <p className="text-xs text-gray-400">No further action is required for this exception.</p>
              </div>
            ) : (
            <>
              <PillToggle
                value={
                  selectedSessions.AM && selectedSessions.PM ? 'FULL'
                  : selectedSessions.AM ? 'AM'
                  : selectedSessions.PM ? 'PM'
                  : ''
                }
                onChange={(val) => setSessionsPreset(val)}
                options={availableSessionOptions}
                variant="text"
                theme={theme}
                ariaLabel="Work session"
                fullWidth
              />
              {(() => {
                // Prefer the rich session_coverage chip for absent_am /
                // absent_pm: it names the actual source (clock event with
                // time + site, leave type with period, or recovery day).
                if (sessionCoverage && sessionCoverage.coverage) {
                  const { covered, coverage } = sessionCoverage;
                  let chipText = null;
                  if (coverage.source === 'leave') {
                    const periodTag = coverage.leave_period === 'FULL'
                      ? 'approved (full-day)'
                      : `approved (${coverage.leave_period} half-day)`;
                    chipText = `${covered} session is covered by ${coverage.leave_type} — ${periodTag}`;
                  } else if (coverage.source === 'recovery') {
                    chipText = `${covered} session is covered by ${coverage.recovery_label}`;
                  } else if (coverage.source === 'clock_event') {
                    const inT = formatClockTime(coverage.clock_in_time);
                    const outT = formatClockTime(coverage.clock_out_time);
                    const site = coverage.work_site_name;
                    const sitePart = site ? ` at ${site}` : '';
                    if (inT && outT) {
                      chipText = `${covered} session is covered by clock-in ${inT} → clock-out ${outT}${sitePart}`;
                    } else if (inT) {
                      chipText = `${covered} session is covered by clock-in at ${inT}${sitePart}`;
                    } else if (outT) {
                      chipText = `${covered} session is covered by clock-out at ${outT}${sitePart}`;
                    }
                  }
                  if (chipText) {
                    return <p className="text-xs text-gray-500">{chipText}</p>;
                  }
                }
                if (amCoveredByLeave || pmCoveredByLeave) {
                  return (
                    <p className="text-xs text-gray-400">
                      {amCoveredByLeave
                        ? `AM session is covered by ${leaveCoveredMap['AM']} (approved)`
                        : `PM session is covered by ${leaveCoveredMap['PM']} (approved)`
                      }
                    </p>
                  );
                }
                return null;
              })()}
            </>
            )}
            {/* Full Day quick path */}
            {(selectedSessions.AM && selectedSessions.PM) && !showSplitSessions ? (
              <div className="space-y-3">
                <div className="p-3 border-2 rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">Full Day</div>
                      <div className="text-sm text-gray-600">
                        {(() => {
                          const start = timeSlots.AM.split(' - ')[0];
                          const sel = (!amIsLeave && !pmIsLeave) ? getSelectedClockOutOption() : null;
                          if (sel?.clock_out_time && sel.is_overtime) {
                            const [hRaw, m] = sel.clock_out_time.split(':').map(Number);
                            const h = hRaw % 24;
                            const nextDay = hRaw >= 24;
                            const period = h < 12 ? 'AM' : 'PM';
                            const h12 = h % 12 || 12;
                            const endLabel = `${h12}:${String(m).padStart(2, '0')} ${period}${nextDay ? ' (next day)' : ''}`;
                            return `${start} - ${endLabel}`;
                          }
                          return `${start} - ${timeSlots.PM.split(' - ')[1]}`;
                        })()}
                      </div>
                    </div>
                    <PillToggle
                      value={(!amIsLeave && !pmIsLeave) ? 'work' : (amIsLeave && pmIsLeave) ? 'leave' : ''}
                      onChange={(val) => {
                        if (val === 'work') { setAmIsLeave(false); setPmIsLeave(false); }
                        else { setAmIsLeave(true); setPmIsLeave(true); if (!amLeaveTypeId) setAmLeaveTypeId(resolveDefaultWorkLeaveType()); if (!pmLeaveTypeId) setPmLeaveTypeId(resolveDefaultWorkLeaveType()); }
                      }}
                      options={[
                        { id: 'work', label: 'Work' },
                        { id: 'leave', label: 'Work Leave' },
                      ]}
                      variant="compact"
                      theme={theme}
                      ariaLabel="Full day: work or work leave"
                    />
                  </div>
                  {/* Show work site for Work mode, leave type for Leave mode */}
                  {(!amIsLeave && !pmIsLeave) ? (
                    <div className="mt-3">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        Work Site
                      </label>
                      <SearchableCombobox
                        items={workSites}
                        value={workSiteIds.AM || workSiteIds.PM}
                        onChange={(siteId) => handleWorkSiteChange('FULL', siteId)}
                        placeholder="Select work site..."
                        searchPlaceholder="Search by site name or project code..."
                        emptyLabel="No work sites found"
                        displayFn={formatSiteLabel}
                        searchFn={workSiteSearchFn}
                        valueFn={(site) => site.id}
                        renderItem={renderWorkSiteItem}
                        disabled={loadingWorkSites}
                        loading={loadingWorkSites}
                        loadingLabel="Loading work sites..."
                        variant="form"
                        theme={theme}
                      />
                      {/* Worker OT: finish-time picker (peers load after work site) */}
                      {isWorkerEmployee && renderPeerOtPicker({ sectionLabel: 'Finish time / OT' })}
                    </div>
                  ) : (
                    <div className="mt-3">
                      <p className="text-xs text-gray-400 mb-2">Only work-status types are available here. For balance-consuming leave (AL, SL, etc.), the employee must submit their own application.</p>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Work Leave Type (Full Day)
                      </label>
                      <FormSelect
                        value={String(amLeaveTypeId || pmLeaveTypeId || '')}
                        onChange={(newValue) => {
                          setAmLeaveTypeId(newValue);
                          setPmLeaveTypeId(newValue);
                        }}
                        options={[
                          { value: '', label: loadingLeaveTypes ? 'Loading leave types...' : 'Select Work Leave Type' },
                          ...availableLeaveTypes.map(t => ({
                            value: String(t.id || t.leave_type_id),
                            label: t.name || t.leave_type_name
                          }))
                        ]}
                        theme={theme}
                        placeholder={loadingLeaveTypes ? 'Loading leave types...' : 'Select Work Leave Type'}
                        disabled={loadingLeaveTypes}
                      />
                      {isEALeaveTypeId(amLeaveTypeId || pmLeaveTypeId) && renderEAFields()}
                    </div>
                  )}
                </div>
              </div>
            ) : (
            <div className="space-y-3">
              {/* AM Session — hidden when covered by approved leave OR when
                  the exception is session-aware absent_pm (AM is already
                  covered by clock event / leave / recovery; rendering an
                  AM card would invite the RO to "fix" a session that has
                  a documented source, contradicting the detector). */}
              {!amCoveredByLeave && exceptionTypeRaw !== 'absent_pm' && (
              <div className="p-3 border-2 rounded-2xl"
                style={{ borderColor: selectedSessions.AM ? (theme === 'admin' ? '#DC2626' : '#1E40AF') : '#E5E7EB' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-600" />
                    <div>
                    <div className="font-medium text-gray-900">AM Session</div>
                      <div className="text-sm text-gray-600">{timeSlots.AM}</div>
                    </div>
                  </div>

                  {selectedSessions.AM && (
                    <PillToggle
                      value={amIsLeave ? 'leave' : 'work'}
                      onChange={(val) => {
                        if (val === 'work') setAmIsLeave(false);
                        else { setAmIsLeave(true); if (!amLeaveTypeId) setAmLeaveTypeId(resolveDefaultWorkLeaveType()); }
                      }}
                      options={[
                        { id: 'work', label: 'Work' },
                        { id: 'leave', label: 'Work Leave' },
                      ]}
                      variant="compact"
                      theme={theme}
                      ariaLabel="AM session: work or work leave"
                    />
                  )}
                  </div>
                  
                  {selectedSessions.AM && (
                  <>
                    {(!amIsLeave) ? (
                      <div className="mt-3">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        Work Site
                      </label>
                        <SearchableCombobox
                          items={workSites}
                          value={workSiteIds.AM}
                          onChange={(siteId) => handleWorkSiteChange('AM', siteId)}
                          placeholder="Select work site..."
                          searchPlaceholder="Search by site name or project code..."
                          emptyLabel="No work sites found"
                          displayFn={formatSiteLabel}
                          searchFn={workSiteSearchFn}
                          valueFn={(site) => site.id}
                          renderItem={renderWorkSiteItem}
                          disabled={loadingWorkSites}
                          loading={loadingWorkSites}
                          loadingLabel="Loading work sites..."
                          variant="form"
                          theme={theme}
                        />
                      </div>
                    ) : (
                      <div className="mt-3">
                        <p className="text-xs text-gray-400 mb-2">Only work-status types are available here. For balance-consuming leave, the employee must submit their own application.</p>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Work Leave Type (AM)</label>
                      <FormSelect
                        value={String(amLeaveTypeId || '')}
                        onChange={(newValue) => setAmLeaveTypeId(newValue)}
                        options={[
                          { value: '', label: loadingLeaveTypes ? 'Loading leave types...' : 'Select Work Leave Type' },
                          ...availableLeaveTypes.map(t => ({
                            value: String(t.id || t.leave_type_id),
                            label: t.name || t.leave_type_name
                          }))
                        ]}
                        theme={theme}
                        placeholder={loadingLeaveTypes ? 'Loading leave types...' : 'Select Work Leave Type'}
                        disabled={loadingLeaveTypes}
                      />
                      {isEALeaveTypeId(amLeaveTypeId) && renderEAFields()}
                    </div>
                    )}
                  </>
                  )}
                  
                  {/* Show location field if AM session has a location value, even when not selected */}
                  {!selectedSessions.AM && locations.AM && (
                    <div className="mt-3 relative location-dropdown-container">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        Location (AM) <span className="text-gray-500 text-xs">- Selected</span>
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-700">
                        {locations.AM}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* PM Session — hidden when covered by approved leave OR
                  when the exception is session-aware absent_am (symmetric
                  to the AM card guard above; PM is already covered, so
                  the RO has nothing to act on for this half). */}
              {!pmCoveredByLeave && exceptionTypeRaw !== 'absent_am' && (
              <div className="p-3 border-2 rounded-2xl"
                style={{ borderColor: selectedSessions.PM ? (theme === 'admin' ? '#DC2626' : '#1E40AF') : '#E5E7EB' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-600" />
                    <div>
                    <div className="font-medium text-gray-900">PM Session</div>
                      <div className="text-sm text-gray-600">{timeSlots.PM}</div>
                    </div>
                  </div>

                  {selectedSessions.PM && (
                    <PillToggle
                      value={pmIsLeave ? 'leave' : 'work'}
                      onChange={(val) => {
                        if (val === 'work') setPmIsLeave(false);
                        else { setPmIsLeave(true); if (!pmLeaveTypeId) setPmLeaveTypeId(resolveDefaultWorkLeaveType()); }
                      }}
                      options={[
                        { id: 'work', label: 'Work' },
                        { id: 'leave', label: 'Work Leave' },
                      ]}
                      variant="compact"
                      theme={theme}
                      ariaLabel="PM session: work or work leave"
                    />
                  )}
                  </div>
                  
                  {selectedSessions.PM && (
                  <>
                    {(!pmIsLeave) ? (
                      <div className="mt-3">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        Work Site
                      </label>
                        <SearchableCombobox
                          items={workSites}
                          value={workSiteIds.PM}
                          onChange={(siteId) => handleWorkSiteChange('PM', siteId)}
                          placeholder="Select work site..."
                          searchPlaceholder="Search by site name or project code..."
                          emptyLabel="No work sites found"
                          displayFn={formatSiteLabel}
                          searchFn={workSiteSearchFn}
                          valueFn={(site) => site.id}
                          renderItem={renderWorkSiteItem}
                          disabled={loadingWorkSites}
                          loading={loadingWorkSites}
                          loadingLabel="Loading work sites..."
                          variant="form"
                          theme={theme}
                        />
                        {isWorkerEmployee && renderPeerOtPicker({ sectionLabel: 'Finish time / OT' })}
                      </div>
                    ) : (
                      <div className="mt-3">
                        <p className="text-xs text-gray-400 mb-2">Only work-status types are available here. For balance-consuming leave, the employee must submit their own application.</p>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Work Leave Type (PM)</label>
                      <FormSelect
                        value={String(pmLeaveTypeId || '')}
                        onChange={(newValue) => setPmLeaveTypeId(newValue)}
                        options={[
                          { value: '', label: loadingLeaveTypes ? 'Loading leave types...' : 'Select Work Leave Type' },
                          ...availableLeaveTypes.map(t => ({
                            value: String(t.id || t.leave_type_id),
                            label: t.name || t.leave_type_name
                          }))
                        ]}
                        theme={theme}
                        placeholder={loadingLeaveTypes ? 'Loading leave types...' : 'Select Work Leave Type'}
                        disabled={loadingLeaveTypes}
                      />
                      {isEALeaveTypeId(pmLeaveTypeId) && renderEAFields()}
                    </div>
                  )}
                  </>
                )}
                
                {/* Show location field if PM session has a location value, even when not selected */}
                {!selectedSessions.PM && locations.PM && (
                  <div className="mt-3 relative location-dropdown-container">
                    <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      Location (PM) <span className="text-gray-500 text-xs">- Selected</span>
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-700">
                      {locations.PM}
                    </div>
                  </div>
                )}
            </div>
              )}
          </div>
            )}
          </section>
          )}

          {/* Additional Information Section */}
          <section className="space-y-4">
            <h4 className="text-base font-semibold text-gray-900">Additional Information</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Reason</label>
                <FormSelect
                  value={reasonCode}
                  onChange={(newValue) => setReasonCode(newValue)}
                  options={isConfirmAbsenceMode ? [
                    { value: '', label: 'Select reason...' },
                    { value: 'no_work_no_leave', label: 'No work performed (no leave)' },
                    { value: 'emergency', label: 'Emergency' },
                    { value: 'other', label: 'Others' },
                  ] : [
                    { value: '', label: 'Select reason...' },
                    { value: 'forgot_clockin_out', label: 'Forgot to clock in/out' },
                    { value: 'forgot_leave_application', label: 'Forgot to apply for leave' },
                    { value: 'system_device_issue', label: 'System/device issue' },
                    { value: 'emergency', label: 'Emergency' },
                    { value: 'other', label: 'Others' },
                  ]}
                  theme={theme}
                  placeholder="Select reason..."
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={`${theme === 'admin' ? 'input-admin' : 'input'} text-base`}
                  placeholder="Brief explanation"
                />
              </div>
            </div>
          </section>
          </>
          )}

          {clockEventConflict && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
              <div className="flex items-start gap-2 text-xs">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-red-800 min-w-0">
                  <p className="font-medium">
                    {clockEventConflict.periods.length > 0
                      ? `${clockEventConflict.periods.join(' and ')} already recorded`
                      : 'Attendance already recorded'}
                  </p>
                  <p className="text-red-700 mt-1">{clockEventConflict.message}</p>
                  {clockEventConflict.events.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {clockEventConflict.events.map((event) => (
                        <li
                          key={event.id}
                          className="flex items-center gap-2 rounded-xl bg-white border border-red-100 px-2.5 py-1.5"
                        >
                          <Clock className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                          <span className="font-medium text-gray-900">
                            {event.type_label} {event.time}
                          </span>
                          <span className="text-gray-500 truncate">
                            {event.source_label}
                            {event.work_site_name ? ` · ${event.work_site_name}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

      </div>
    </Modal>
  );
};

export default ROExceptionModal;

